import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { LuUsers, LuUserPlus } from 'react-icons/lu';
import AppButton from '@/components/atoms/AppButton';
import GlobalSearch from '@/components/atoms/GlobalSearch';
import { PROVIDER_CONSTANT_PROPS } from '@/constant/provider/provider';
import { useToast } from '@/components/organism/Toast/ToastProvider';
import FullPageCard from '@/components/organism/Cards/FullPageCard';
import _, { isEmpty } from 'lodash';
import { fetchUsers } from '@/services/user';
import {
  getUsersFailure,
  getUsersSuccess,
  setShouldRefetchUsers,
} from '@/store/slices/user.slice';
import { IUser } from '@/services/user/user.type';
import { fetchUserRoles, getRoleNames } from '@/services/role';
import {
  getUsersRoleFailure,
  getUsersRoleSuccess,
} from '@/store/slices/role.slice';
import { getUserrDetails } from '@/store/selectors/user.selector';
import {
  ROLES,
  USER_CONSTANT_PROPS,
  USER_PAGE_SEARCH_PLACEHOLDER_TEXT,
  USER_PROPS,
  getRole,
} from '@/constant/users/user.constant';
import { useDebounce } from '@/hooks/useDebounce';
import {
  ROW_PER_PAGE_OPTION,
  authorizeRole,
  pagination as PAGINATION_INITIAL_STATE,
  STRINGS,
} from '@/constant/global/global.constant';
import useDeepEffect from '@/hooks/useDeepEffect';
import { DataTable, type ColumnDef } from '@/components/organism/Table';
import PageToolbar from '@/components/organism/Cards/PageToolbar';
import { NAV_ITEMS, getNavItemIcon } from '@/constant/layout/layout.constant';
import { buildColumns } from './user.columns';
import Forbidden from '../Forbidden';
import CreateUser from './CreateUser';
import { tid } from '@/utils/testId';

const UserComponent = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isCopy, setIsCopy] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [pagination, setPagination] = useState(PAGINATION_INITIAL_STATE);
  const [search, onSearch] = useState('');
  const [sorting, setSorting] = useState({
    orderBy: 'updatedOn',
    order: 'desc',
  });
  const [rolesInfo, setRolesInfo] = useState([]);
  const dispatch = useDispatch();
  const toast = useToast();
  const [isDataFound, setIsDataFound] = useState(true);
  const {
    loading,
    users: { data, pagination: paginationResponse },
    shouldRefetchUsers,
  } = useSelector(getUserrDetails);
  const trimmedSearch = search?.trim();
  const searchQuery = useDebounce(trimmedSearch, 1500);
  const userHasReadAccess = _.includes(authorizeRole.PORTAL.DELETE, getRole());
  const totalPages = Math.ceil(
    paginationResponse?.totalRecords / pagination.limit,
  );
  const [isStateMutated, setIsStateMutated] = useState(false);

  const userHasWriteAccess = _.includes(authorizeRole.USERS.CREATE, getRole());

  const handlePageSizeChange = (value: number) => {
    setPagination({ ...pagination, limit: value });
    if (totalPages < pagination?.page) {
      setPagination({ page: totalPages, limit: value });
    }
  };

  const handlePreviousClick = (page: number) => {
    if (page > 1 && page <= totalPages) {
      setPagination({ ...pagination, page: page - 1 });
    }
  };

  const handleNextClick = (page: number) => {
    if (page >= 1 && page < totalPages) {
      setPagination({ ...pagination, page: page + 1 });
    }
  };

  const onCopyClick = () => {
    setIsCopy(true);
    setTimeout(() => {
      setIsCopy(false);
    }, 500);
  };

  const handleOnAddClick = () => {
    setIsOpen(true);
  };

  useEffect(() => {
    if (pagination?.page > totalPages) {
      setPagination({ ...pagination, page: 1 });
    }
  }, [totalPages]);

  const getUserRoles = async () => {
    try {
      const res = await fetchUserRoles({ ...pagination });
      setRolesInfo((res as any)?.data?.data);
      dispatch(getUsersRoleSuccess((res as any)?.data));
    } catch (error) {
      dispatch(getUsersRoleFailure(undefined));
      toast?.pushError(
        (error as any)?.response?.data?.message,
        PROVIDER_CONSTANT_PROPS.GENERIC.TOAST_TIME,
      );
    }
  };

  useDeepEffect(
    () => {
      setIsStateMutated(true);
    },
    [searchQuery, pagination, sorting, shouldRefetchUsers],
    true,
  );

  const isUserDataEmpty = isEmpty(search) && data?.length === 0;
  const getUsersList = async () => {
    try {
      if (
        trimmedSearch?.length >= USER_CONSTANT_PROPS.GENERIC.MIN_QUERY_LENGTH
      ) {
        const res = await fetchUsers({
          ...sorting,
          ...pagination,
          keywords: searchQuery,
        });
        dispatch(getUsersSuccess((res as any)?.data));
        (res as any)?.data?.data?.length
          ? setIsDataFound(true)
          : setIsDataFound(false);
      } else if (isEmpty(search)) {
        const res = await fetchUsers({
          ...sorting,
          ...pagination,
        });
        dispatch(getUsersSuccess((res as any)?.data));
        (res as any)?.data?.data?.length
          ? setIsDataFound(true)
          : setIsDataFound(false);
      }
    } catch (error) {
      dispatch(getUsersFailure(undefined));
      toast?.pushError(
        (error as any)?.response?.data?.message,
        PROVIDER_CONSTANT_PROPS.GENERIC.TOAST_TIME,
      );
    }
  };

  useEffect(() => {
    if (isStateMutated) {
      getUsersList();
    }
  }, [searchQuery, pagination, sorting]);
  useEffect(() => {
    if (shouldRefetchUsers) {
      getUsersList();
      dispatch(setShouldRefetchUsers(false));
    }
  }, [shouldRefetchUsers, dispatch, getUsersList]);

  useEffect(() => {
    getUserRoles();
  }, []);

  return !userHasReadAccess ? (
    <Forbidden />
  ) : (
    <FullPageCard
      className="rounded-b-none"
      header={
        <PageToolbar
          title={STRINGS.USER}
          subtitle={STRINGS.USER_SUB}
          icon={getNavItemIcon(NAV_ITEMS.USERS)}
          count={paginationResponse?.totalRecords}
          actionsSlot={
            <GlobalSearch
              className="w-[240px]"
              placeholder={USER_PAGE_SEARCH_PLACEHOLDER_TEXT}
              globalSearch={search}
              setGlobalSearch={onSearch}
              isDataFound={isDataFound}
              disabled={isUserDataEmpty}
            />
          }
          actionButtons={
            getRole() === ROLES.ADMIN ? (
              <AppButton
                variant="primary"
                size="sm"
                icon={<LuUserPlus className="w-3.5 h-3.5" />}
                onClick={handleOnAddClick}
              >
                {USER_PROPS.CREATE_USER}
              </AppButton>
            ) : null
          }
        />
      }
    >
      <DataTable<IUser>
        data-testid={tid('user-table')}
        data={data as unknown as IUser[]}
        loading={loading}
        skeletonRows={15}
        columns={buildColumns(isCopy, onCopyClick, rolesInfo)}
        onSort={(orderBy: any, order: any) => setSorting({ orderBy, order })}
        sortState={sorting as any}
        pagination={
          !isEmpty(data)
            ? {
              state: pagination,
              totalItem: paginationResponse?.totalRecords,
              rowsPerPageOptions: ROW_PER_PAGE_OPTION,
              onRowsPerPageChange: handlePageSizeChange,
              onNextClick: handleNextClick,
              onPreviousClick: handlePreviousClick,
            }
            : null
        }
        rowKey={(item) => (item as any)?.userId ?? Math.random().toString()}
      />

      {isOpen && (
        <CreateUser
          data-testid={tid('user-create-modal')}
          isOpen={isOpen}
          onClick={(val) => setIsSubmitting(val)}
          onClose={() => setIsOpen(false)}
        />
      )}
    </FullPageCard>
  );
};

export default UserComponent;
